import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import update from 'immutability-helper';
import { ArrowLeft } from 'lucide-react';

// Mixins / Modules
import isResizable from '../../../../mixins/resizable';
import Window from '../../../../../modules/window';

// Models
import Query from '@/models/query';

// Base
import Button from '../../../base/button';
import Col from '../../../base/col';
import GoogleButton from '../../../base/google_button';
import Row from '../../../base/row';
import Separator from '../../../base/separator';

// Form
import Checkbox from '../../../form/checkbox';
import Input from '../../../form/input';
import LogoutLogo from '../../../agents/subscribe/logout_logo';
import PasswordStrengthChecklist from '../../../form/password_strength_checklist';

// Related Components
import AvatarUpload from './avatar_upload';
import Turnstile from './turnstile';

class Consultant extends React.Component {
  static propTypes = {
    user: PropTypes.object.isRequired,
    onSetView: PropTypes.func.isRequired,
    onSignUp: PropTypes.func.isRequired,
    onSignIn: PropTypes.func,
  }

  constructor(props) {
    super(props);

    this.state = {
      user:
        props.user,
      errorMessages:
        {},
      sending:
        false,
      turnstileToken:
        null,
      turnstileResetKey:
        0,
    }
  }

  handleInputChange = (
    field,
    value
  ) => {
    const { user } =
      this.state;

    const newUser =
      update(
        user,
        {
          [field]: {
            $set: value,
          },
        }
      );

    this.setState({
      user: newUser,
    });
  }

  handleProfileInputChange = (
    field,
    value
  ) => {
    const { user } =
      this.state;

    const newUser =
      update(
        user,
        {
          profile_attributes: {
            [field]: {
              $set: value,
            },
          },
        }
      );

    this.setState({
      user: newUser,
    });
  }

  handleValidate = () => {
    this.setState({
      sending: true,
    });
  }

  handleError = () => {
    this.setState({
      sending: false,
    });
  }

  handleTurnstileTokenChange = (turnstileToken) => {
    this.setState((state) => ({
      turnstileToken,
      errorMessages: {
        ...state.errorMessages,
        turnstile: turnstileToken ? null : state.errorMessages.turnstile,
      },
    }));
  }

  handleSubmit = (e) => {
    const { onSignUp } =
      this.props;

    const {
      sending,
      turnstileToken,
      user,
    } = this.state;

    e && e.preventDefault();

    if (sending) return;

    if (!turnstileToken) {
      this.setState({
        errorMessages: {
          turnstile: [
            'Please complete the verification.',
          ],
        },
      });

      return;
    }

    this.setState(
      {
        sending: true,
      },
      () => {
        const data = {
          turnstile_token:
            turnstileToken,
          user: {
            email:
              user.email,
            password:
              user.password ||
              '',
            user_type:
              user.user_type,
            allow_marketing:
              user.allow_marketing,
            profile_attributes:
              user.profile_attributes,
            invitation_token:
              user.invitation_token,
          },
        };

        if (
          user.consent_form_id
        ) {
          data.consent_form_id =
            user.consent_form_id;
        }

        if (
          user.invitation_token
        ) {
          data.invitation_token =
            user.invitation_token;
        }

        Query
          .request(
            '/users.json',
            {
              method: 'POST',
              body: data,
            }
          )
          .then((response) => {
            gtag(
              'event',
              'conversion',
              {
                'send_to':
                  'AW-707878932/-M1tCP7KxKsBEJTAxdEC',
              }
            );

            const {
              user,
              location,
            } = response;

            onSignUp(
              location,
              user
            );
          })
          .catch(
            ({
              responseJSON,
            }) => {
              this.setState(
                (state) => ({
                  errorMessages:
                    (
                      responseJSON ||
                      {}
                    ).errors ||
                    {},
                  sending:
                    false,
                  turnstileToken:
                    null,
                  turnstileResetKey:
                    state.turnstileResetKey +
                    1,
                })
              );
            }
          );
      }
    );
  }

  handleGoogleAuth = (
    response
  ) => {
    const { onSignUp } =
      this.props;

    const {
      user,
      location,
    } = response;

    if (
      user.user_type ===
      'consultant'
    ) {
      Window.redirect(
        location
      );
    } else {
      onSignUp(
        location,
        user
      )
    }
  }

  render() {
    const {
      onSetView,
      onSignIn,
    } = this.props;

    const {
      user,
      sending,
      errorMessages,
      turnstileResetKey,
    } = this.state;

    return (
      <React.Fragment>
        <div className='height-60 margin-bottom-70 relative'>
          <LogoutLogo />

          <div className='absolute right-30'>
            Have an account?{' '}

            <Button
              {...{
                ...(
                  onSignIn
                    ? {
                      onClick:
                        onSignIn,
                    }
                    : {
                      to:
                        '/users/sign_in',
                    }
                ),
                className:
                  'sm-font margin-top-30 underline',
                text:
                  'Log in',
                size:
                  'custom',
              }}
            />
          </div>
        </div>

        <div className='width-560 margin-center relative'>
          <div className='absolute left--45'>
            <ArrowLeft
              {...{
                size:
                  24,
                onClick:
                  () =>
                    onSetView(
                      'user_type'
                    ),
                className:
                  'cursor-pointer',
              }}
            />
          </div>

          <div className='margin-bottom-30 xlg-font semi-bold line-height-26'>
            Create Your Free Account
          </div>

          <div className='width-520'>
            {
              !user.invitation_token &&
              (
                <React.Fragment>
                  <div className='margin-bottom-20 width-100pct flex'>
                    <div className='margin-right-auto margin-left-auto'>
                      <GoogleButton
                        {...{
                          text:
                            'signup_with',
                          onAuth:
                            this.handleGoogleAuth,
                          onValidate:
                            this.handleValidate,
                          onError:
                            this.handleError,
                          userType:
                            'consultant',
                        }}
                      />
                    </div>
                  </div>

                  <div className='flex justify-space-between vertical-align margin-bottom-20'>
                    <div>
                      <Separator className='width-155 margin-top-14 margin-bottom-14' />
                    </div>

                    <div>or</div>

                    <div>
                      <Separator className='width-155 margin-top-14 margin-bottom-14' />
                    </div>
                  </div>
                </React.Fragment>
              )
            }

            <form
              onSubmit={
                this.handleSubmit
              }
            >
              <Row nested>
                <Col s={12}>
                  <AvatarUpload
                    {...{
                      value:
                        user.profile_attributes.remote_avatar_url,
                      onChange:
                        (value) =>
                          this.handleProfileInputChange(
                            'remote_avatar_url',
                            value
                          ),
                    }}
                  />
                </Col>

                <Col s={12} m={6}>
                  <Input
                    {...{
                      id:
                        'first_name',
                      label:
                        'First Name',
                      value:
                        user.profile_attributes.first_name,
                      required:
                        true,
                      errorMessage:
                        errorMessages['profile.first_name'],
                      onChange:
                        (_, value) =>
                          this.handleProfileInputChange(
                            'first_name',
                            value
                          ),
                    }}
                  />
                </Col>

                <Col s={12} m={6}>
                  <Input
                    {...{
                      id:
                        'last_name',
                      label:
                        'Last Name',
                      value:
                        user.profile_attributes.last_name,
                      required:
                        true,
                      errorMessage:
                        errorMessages['profile.last_name'],
                      onChange:
                        (_, value) =>
                          this.handleProfileInputChange(
                            'last_name',
                            value
                          ),
                    }}
                  />
                </Col>

                <Col s={12}>
                  <Input
                    {...{
                      id:
                        'signup_email',
                      label:
                        'Email address',
                      value:
                        user.email,
                      required:
                        true,
                      errorMessage:
                        errorMessages['email'],
                      onChange:
                        (_, value) =>
                          this.handleInputChange(
                            'email',
                            value
                          ),
                      disabled:
                        !!user.invitation_token,
                    }}
                  />
                </Col>

                <Col s={12}>
                  <Input
                    {...{
                      id:
                        'signup_password',
                      label:
                        'Create Password',
                      value:
                        user.password ||
                        '',
                      required:
                        true,
                      errorMessage:
                        errorMessages['password'],
                      type:
                        'password',
                      onChange:
                        (_, value) =>
                          this.handleInputChange(
                            'password',
                            value
                          ),
                      className:
                        classNames(
                          '',
                          {
                            'margin-bottom-10':
                              !errorMessages.password,
                          }
                        ),
                      allowPasswordToggle:
                        true,
                    }}
                  />

                  <PasswordStrengthChecklist
                    {...{
                      password:
                        user.password ||
                        '',
                    }}
                  />
                </Col>

                <Col s={12}>
                  <Checkbox
                    id='allow_marketing'
                    className='xxsm-font'
                    checked={
                      user.allow_marketing
                    }
                    onChange={
                      (_, checked) =>
                        this.handleInputChange(
                          'allow_marketing',
                          checked
                        )
                    }
                    label='I want to stay looped in with helpful newsletters, product updates and events.'
                  />
                </Col>

                <Col s={12}>
                  <Turnstile {...{
                    errorMessage:
                      errorMessages.turnstile,
                    onTokenChange:
                      this.handleTurnstileTokenChange,
                    resetKey:
                      turnstileResetKey,
                  }} />
                </Col>

                <Col s={12}>
                  <Button
                    id='continue'
                    text='Continue'
                    onClick={
                      this.handleSubmit
                    }
                    className='height-45 med-font vertical-align line-height-45'
                    block
                    disabled={
                      sending
                    }
                  />
                </Col>
              </Row>

              <button
                type='submit'
                className='hide'
              />
            </form>
          </div>

          <div className='margin-bottom-20 xxsm-font'>
            <React.Fragment>
              By signing up, you are agreeing to our

              <a
                className='primary-color hover-dark-primary'
                target='_blank'
                href='/terms_and_conditions'
              >
                {' '}Terms &amp; Conditions
              </a>.
            </React.Fragment>
          </div>
        </div>
      </React.Fragment>
    )
  }
}

export default isResizable(
  Consultant
);
